import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as PlusIcon,
  ExpandMore as ExpandMoreIcon,
  EditOutlined,
  DeleteOutline,
  ScienceOutlined,
  StraightenOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { message, Empty } from 'antd';

import { getMeasurements, removeMeasurementParameters } from '../../../../helpers/api';
import { parseString3, parseString4 } from '../../../../helpers/utils';
import AddMeasurementModal from './AddMeasurement';
import AddParameterModal from './AddParameter';
import EditMeasurementModal from './EditMeasurement';
import EditParameter from './EditParameter';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { openNotification } from '../../../../helpers/notification';

const MeasurmentConfig = () => {
  const [addModal, setAddModal] = useState(false);
  const [reload, setReload] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openParameterModal, setOpenParameterModal] = useState(false);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState('');
  const [openEditMeasurement, setOpenEditMeasurement] = useState(false);
  const [selectedMeasurementName, setSelectedMeasurementName] = useState('');
  const [editParameterModal, setEditParameterModal] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);

  // States for MUI Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState({ parameterId: '', measurementId: '' });

  const [measurementData, setMeasurementData] = useState([]);
  const [measurementLoading, setMeasurementLoading] = useState(false);
  const [parameterLoading, setParameterLoading] = useState(false);

  useEffect(() => {
    fetchMeasurementData();
  }, [reload]);

  const fetchMeasurementData = () => {
    setMeasurementLoading(true);
    getMeasurements()
      .then((res) => {
        const data = res.result.data || [];
        setMeasurementData(data);
        if (data.length > 0 && !expanded) {
          setExpanded(data[0].measurementId);
        }
      })
      .catch((error) => {
        message.error(error?.data?.result?.status?.desc || 'Failed to load data');
      })
      .finally(() => {
        setMeasurementLoading(false);
        setReload(false);
      });
  };

  const openDeleteDialog = (parameterId, measurementId) => {
    setItemToDelete({ parameterId, measurementId });
    setDeleteDialogOpen(true);
  };

  const handleRemoveParameter = () => {
    const { parameterId, measurementId } = itemToDelete;

    // console.log('parameterId', parameterId);
    // console.log('measurementId', measurementId);
    setParameterLoading(true);
    const payload = {
      parameterId: parameterId,
      measurementId: measurementId,
    };

    removeMeasurementParameters(payload)
      .then((res) => {
        console.log('res', res);
        message.success('Parameter removed successfully');
        setReload(true); // Triggers screen refresh via useEffect
        setDeleteDialogOpen(false);
      })
      .catch((error) => {
        // console.log('error', error);
        openNotification({
          type: 'error',
          message: error?.data?.result?.status?.desc || 'Something went wrong',
        });
      })
      .finally(() => setParameterLoading(false));
  };

  const renderParametersTable = (parameters, measurementId) => {
    const columns = [
      {
        name: 'unit',
        label: 'Parameter Name',
        options: {
          customBodyRender: (val) => (
            <Typography variant="body2" fontWeight={600}>
              {parseString4(parseString3(val))}
            </Typography>
          ),
        },
      },
      {
        name: 'siUnit',
        label: 'SI Unit',
        options: {
          customBodyRender: (val) => (
            <Typography variant="body2" color="text.secondary">
              {parseString4(parseString3(val)) || '-'}
            </Typography>
          ),
        },
      },
      {
        name: 'nesrea',
        label: 'NESREA Limit',
        options: {
          customBodyRender: (val, tableMeta) => {
            const rowData = parameters[tableMeta.rowIndex];
            const symbol =
              rowData.nesreaDataType === 'LESS_THAN'
                ? '<'
                : rowData.nesreaDataType === 'GREATER_THAN'
                ? '>'
                : '';
            return <Typography variant="body2">{!val ? '-' : `${symbol} ${val}`}</Typography>;
          },
        },
      },
      {
        name: 'world',
        label: 'World Bank Limit',
        options: {
          customBodyRender: (val, tableMeta) => {
            const rowData = parameters[tableMeta.rowIndex];
            const symbol =
              rowData.worldDataType === 'LESS_THAN'
                ? '<'
                : rowData.worldDataType === 'GREATER_THAN'
                ? '>'
                : '';
            return <Typography variant="body2">{!val ? '-' : `${symbol} ${val}`}</Typography>;
          },
        },
      },
      {
        name: 'parameterId',
        label: ' ',
        options: {
          customBodyRender: (val) => (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => {
                    const param = parameters.find((p) => p.parameterId === val);
                    setSelectedParameter(param);
                    setEditParameterModal(true);
                  }}
                >
                  <EditOutlined fontSize="small" color="info" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => openDeleteDialog(val, measurementId)}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        },
      },
    ];

    return (
      <MUIDataTable
        data={parameters}
        columns={columns}
        options={{
          elevation: 0,
          selectableRows: 'none',
          search: false,
          filter: false,
          download: false,
          print: false,
          viewColumns: false,
          pagination: false,
          setTableProps: () => ({
            size: 'small',
            sx: { '& .MUIDataTableToolbar-root': { display: 'none' } },
          }),
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
        }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, bgcolor: '#F4F6F8', minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  bgcolor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  display: 'flex',
                }}
              >
                <StraightenOutlined fontSize="small" />
              </Box>
              <Typography variant="h5" fontWeight={700}>
                Measurement
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage environmental measurement types and their respective parameters.
            </Typography>
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<PlusIcon />}
            onClick={() => setAddModal(true)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3 }}
          >
            New Measurement
          </Button>
        </Stack>
      </Paper>

      {measurementLoading && !reload ? (
        <Box sx={{ p: 4, bgcolor: '#fff', borderRadius: '16px' }}>
          <LoaderSkeleton />
          <Divider sx={{ my: 3 }} />
          <LoaderSkeleton />
        </Box>
      ) : measurementData.length === 0 ? (
        <Box sx={{ py: 10, bgcolor: '#fff', borderRadius: '16px', textAlign: 'center' }}>
          <Empty description="No measurement types found" />
        </Box>
      ) : (
        <Box>
          {measurementData.map((item) => (
            <Accordion
              key={item.measurementId}
              expanded={expanded === item.measurementId}
              onChange={() =>
                setExpanded(expanded === item.measurementId ? false : item.measurementId)
              }
              sx={{
                mb: 2,
                borderRadius: '16px !important',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: expanded === item.measurementId ? alpha('#1976d2', 0.03) : '#fff' }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <ScienceOutlined color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                    {item.measurementName}
                  </Typography>
                  <Tooltip title="Edit Type Name">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMeasurementName(item.measurementName);
                        setSelectedMeasurementId(item.measurementId);
                        setOpenEditMeasurement(true);
                      }}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ p: 2 }}>
                  {renderParametersTable(item.parameters || [], item.measurementId)}
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button
                      size="small"
                      startIcon={<PlusIcon />}
                      onClick={() => {
                        setSelectedMeasurementId(item.measurementId);
                        setOpenParameterModal(true);
                      }}
                      sx={{ fontWeight: 700, textTransform: 'none' }}
                    >
                      Add Parameter
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* --- DELETE CONFIRMATION DIALOG --- */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !parameterLoading && setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberOutlined color="error" />
          <Typography variant="h6" fontWeight={700}>
            Confirm Removal
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove this parameter? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={parameterLoading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveParameter}
            variant="contained"
            color="error"
            disableElevation
            disabled={parameterLoading}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, minWidth: '100px' }}
          >
            {parameterLoading ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals maintained for functionality */}
      {addModal && (
        <AddMeasurementModal
          open={addModal}
          close={() => setAddModal(false)}
          reloadMeasurements={() => setReload(true)}
        />
      )}
      {openParameterModal && (
        <AddParameterModal
          open={openParameterModal}
          close={() => setOpenParameterModal(false)}
          reloadMeasurements={() => setReload(true)}
          id={selectedMeasurementId}
        />
      )}
      {openEditMeasurement && (
        <EditMeasurementModal
          open={openEditMeasurement}
          close={() => setOpenEditMeasurement(false)}
          reloadMeasurements={() => setReload(true)}
          id={selectedMeasurementId}
          name={selectedMeasurementName}
        />
      )}
      {editParameterModal && (
        <EditParameter
          open={editParameterModal}
          close={() => setEditParameterModal(false)}
          data={selectedParameter}
          reloadMeasurements={() => setReload(true)}
        />
      )}
    </Box>
  );
};

export default MeasurmentConfig;
